// Enterprise side of the join-enterprise request cycle — /enterprise/
// join-requests/**, ENTERPRISE-only server-side (lendcord-core's
// JoinEnterpriseRequestService.requireEnterpriseCaller, LEND-111).

import { baseApi } from "@/app/store/baseApi";
import type { Profile } from "@/app/profile/profile.types";
import type {
  JoinEnterpriseDecisionRequest,
  JoinEnterpriseDecisionResponse,
  JoinEnterpriseRequestListItem,
  JoinEnterpriseStatusFilter,
} from "../joinRequests.types";

export const joinRequestsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listJoinRequests: builder.query<JoinEnterpriseRequestListItem[], JoinEnterpriseStatusFilter | undefined>({
      query: (status) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        return { url: "/enterprise/join-requests", method: "GET", params, auth: true };
      },
      providesTags: (requests) =>
        requests
          ? [
              { type: "JoinEnterpriseRequest" as const, id: "LIST" },
              ...requests.map((r) => ({ type: "JoinEnterpriseRequest" as const, id: r.userId })),
            ]
          : [{ type: "JoinEnterpriseRequest" as const, id: "LIST" }],
    }),

    // Reuses ProfileService.getProfile's exact shape server-side, scoped so
    // the caller can only view a user who actually applied to them (404
    // LEND-110 otherwise) — "view profile" before deciding.
    getJoinRequestApplicantProfile: builder.query<Profile, string>({
      query: (userId) => ({ url: `/enterprise/join-requests/${userId}/profile`, auth: true }),
    }),

    decideJoinRequest: builder.mutation<
      JoinEnterpriseDecisionResponse,
      { userId: string; body: JoinEnterpriseDecisionRequest }
    >({
      query: ({ userId, body }) => ({
        url: `/enterprise/join-requests/${userId}/decision`,
        method: "POST",
        body,
        auth: true,
      }),
      invalidatesTags: [{ type: "JoinEnterpriseRequest", id: "LIST" }],
    }),
  }),
});

export const { useListJoinRequestsQuery, useGetJoinRequestApplicantProfileQuery, useDecideJoinRequestMutation } =
  joinRequestsApi;
